import { Link } from "react-router-dom"
import { Phone, Target, RefreshCw, CheckCircle, TrendingUp, Zap, Star, Globe } from "lucide-react"
import { useLanguage } from "../context/LanguageContext"

const LandingPage = () => {
  const { language, toggleLanguage } = useLanguage()
  
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Bar */}
      <nav className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img src="/New_Logo.png" alt="Neuro Train BOT" className="h-12" />
              <span className="text-xl font-bold text-[#0A4B4B]">Neuro Train BOT</span>
            </div>
            <div className="flex items-center space-x-4">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className={language === 'en' 
                  ? "flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-bold bg-orange-500 text-white hover:bg-orange-600 transition"
                  : "flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-bold bg-blue-500 text-white hover:bg-blue-600 transition"
                }
              >
                <Globe className="h-4 w-4" />
                <span>{language === 'en' ? 'ESPAÑOL' : 'ENGLISH'}</span>
              </button>
              <Link to="/login" className="text-gray-600 hover:text-emerald-600 font-medium">
                {language === 'es' ? 'Iniciar Sesión' : 'Sign In'}
              </Link>
              <Link to="/register">
                <button className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-medium transition">
                  {language === 'es' ? 'Suscribirse' : 'Subscribe Now'}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Background Image */}
      <section className="relative overflow-hidden">
        {/* Background Image Container - Add your hero-background.jpg here */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/hero-background.jpg" 
            alt="Call Center Professional" 
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            {/* Main Title */}
            <h1 className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500 mb-2" style={{fontFamily: "Inter, system-ui, sans-serif", letterSpacing: "-0.02em"}}>
              Neuro Train BOT
            </h1>
            
            {/* Subtitle */}
            <h2 className="text-2xl md:text-3xl font-semibold text-emerald-600 mb-4">
              {language === 'es' ? 'Evaluador de Llamadas y Transcripción' : 'Caller Evaluator and Transcription'}
            </h2>
            
            {/* Tagline */}
            <p className="text-xl text-gray-800 font-medium mb-6">
              {language === 'es' ? 'Lleva tu Servicio al Cliente al Siguiente Nivel' : 'Take your Customer Service to the Next Level'}
            </p>
            
            <p className="text-lg text-gray-600 mb-8">
              {language === 'es' 
                ? 'Análisis impulsado por IA que transforma el rendimiento de tu equipo de ventas. Aumenta las conversiones en un 40%+ con información en tiempo real y evaluaciones completas de llamadas.'
                : 'AI-powered analysis that transforms your sales team performance. Boost conversions by 40%+ with real-time insights and comprehensive call evaluations.'}
            </p>

            {/* Sign In text above buttons */}
            <p className="text-sm text-gray-500 mb-3">
              {language === "es" ? "¿Ya tienes una cuenta?" : "Already have an account?"} <Link to="/login" className="text-emerald-600 hover:underline font-medium">{language === "es" ? "Iniciar sesión" : "Sign in"}</Link>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <button className="bg-emerald-600 hover:bg-emerald-700 text-white px-10 py-4 rounded-xl font-semibold text-lg transition shadow-lg hover:shadow-xl hover:scale-105">
                {language === "es" ? "Comenzar Ahora" : "Get Started Today"}
                </button>
              </Link>
            </div>
          </div>

          {/* Optional: Side Dashboard Image */}
          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 hidden lg:block">
            <img 
              src="/images/dashboard-analytics.jpg" 
              alt="Analytics Dashboard" 
              className="w-96 rounded-l-2xl shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Services Section with Real Images */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === "es" ? "Analiza Cada Tipo de Llamada" : "Analyze Every Type of Call"}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === "es" ? "Desde llamadas en frío hasta servicio al cliente, análisis completo para cada interacción" : "From cold outreach to customer service, comprehensive analysis for every interaction"}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                title: language === 'es' ? "Llamadas en Frío" : "Cold Calls", 
                desc: language === 'es' ? "Domina el arte de las primeras impresiones" : "Master the art of first impressions",
                image: "/images/cold-call-agent.jpg",
                icon: Phone 
              },
              { 
                title: language === 'es' ? "Prospectos Calientes" : "Warm Leads", 
                desc: language === 'es' ? "Convierte prospectos interesados" : "Convert interested prospects",
                image: "/images/handshake-meeting.jpg",
                icon: Target 
              },
              { 
                title: language === 'es' ? "Seguimientos" : "Rehash Calls", 
                desc: language === 'es' ? "Recupera oportunidades perdidas" : "Win back lost opportunities",
                image: "/images/team-discussion.jpg",
                icon: RefreshCw 
              },
              { 
                title: language === 'es' ? "Confirmaciones" : "Confirmations", 
                desc: language === 'es' ? "Asegura las citas" : "Lock in appointments",
                image: "/images/calendar-scheduling.jpg",
                icon: CheckCircle 
              }
            ].map((service, index) => (
              <div key={index} className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition overflow-hidden">
                {/* Service Image Container */}
                <div className="h-48 bg-gray-100 relative overflow-hidden">
                  {/* Add actual image here */}
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
                    onError={(e) => {
                      // Fallback if image doesn't exist
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  {/* Fallback icon display */}
                  <div className="hidden w-full h-full items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
                    <service.icon className="h-16 w-16 text-emerald-600" />
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Simple CTA Section */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {language === "es" ? "Comienza a Mejorar el Rendimiento de tus Llamadas Hoy" : "Start Improving Your Call Performance Today"}
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            {language === "es" ? "Únete a más de 500 equipos que ya usan Neuro Train BOT" : "Join 500+ teams already using Neuro Train BOT"}
          </p>
          <Link to="/register">
            <button className="bg-white text-emerald-600 px-8 py-3 rounded-lg font-semibold text-lg hover:shadow-lg transition">
              {language === "es" ? "Suscribirse Ahora" : "Subscribe Now"}
            </button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <p className="text-gray-400 mb-2">
              Created with ❤️ by{" "}
              <span className="font-semibold text-white">Felipe Jansen</span> •{" "}
              <span className="font-semibold text-white">Patagon Consulting Inc</span>
            </p>
            <p className="text-sm text-gray-500 mb-4">
              © 2025 Neuro Train BOT from Patagon Consulting Inc. {language === "es" ? "Todos los derechos reservados." : "All rights reserved."}
            </p>
            <div className="flex justify-center space-x-6">
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm">
                {language === "es" ? "Términos y Condiciones" : "Terms & Conditions"}
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm">
                {language === "es" ? "Política de Privacidad" : "Privacy Policy"}
              </Link>
              <a href="mailto:hello@patagonusa.com" className="text-gray-400 hover:text-white text-sm">
                {language === "es" ? "Contacto" : "Contact"}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
